<script lang="ts">
	import { marketsConfig } from '$lib/config/markets';
	import { getDisplayName, getTokenLogo } from '$lib/perps';
	import type { Trade } from '$lib/types/trade';
	import GradientBorder from '$lib/util/GradientBorder.svelte';
	import { getNumBackInUnits, getDate, getTime, addDollarSign } from '$lib/util/util';
	import { optimism } from '@wagmi/core/chains';
	import BigNumber from 'bignumber.js';
	import { getDisplayAsset } from '../tradingView/util';

	export let tra: Trade;
</script>

<!-- Desktop View -->
<a href="{optimism.blockExplorers?.default.url}/tx/{tra.transactionHash}" target="blank">
	<div
		class="hidden sm:grid grid-cols-7 border-t border-t-fx-grey-600 py-4 items-center hover:bg-fx-grey-900 active:bg-fx-grey-700 cursor-pointer px-2 rounded-lg"
	>
		<div class="flex flex-col gap-1 items-start justify-start">
			<div class="text-fx-grey-100 text-xs font-medium text-center">{getDate(tra.timestamp)}</div>
			<div class="text-fx-grey-300 text-xs font-medium text-center">{getTime(tra.timestamp)}</div>
		</div>
		<div class="flex gap-2">
			<img loading="lazy" src={getTokenLogo(tra.marketIdentifier)} alt="logo" class="w-5 h-5 rounded-full" />
			<div class="text-fx-grey-100 text-sm rounded-lg font-medium">
				{getDisplayName(tra.marketIdentifier)}
			</div>
		</div>
		<div class="flex">
			<GradientBorder
				gradient="bg-fx-gradient-6"
				paddingClass="p-px"
				borderRadius="rounded-lg "
				outerClassList="w-fit my-auto mx-auto h-fit"
				innerClassList="/''bg-fx-black"
			>
				{#if tra.orderType === 'Liquidation'}
					<div
						class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-16 text-xs font-medium rounded-lg"
					>
						{tra.orderType}
					</div>
				{:else if new BigNumber(tra.size).gt(0)}
					<div
						class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-23 text-xs font-medium rounded-lg"
					>
						Long
					</div>
				{:else}
					<div
						class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-22 text-xs font-medium rounded-lg"
					>
						Short
					</div>
				{/if}
			</GradientBorder>
		</div>

		<div class="flex flex-col gap-1 items-center justify-center mx-auto">
			<div class="flex flex-col gap-1 items-center justify-center mx-auto">
				<div class="text-fx-grey-100 text-xs font-medium text-center">
					{getNumBackInUnits(tra.size, 18, 4)}
					{getDisplayAsset(tra.asset)}
				</div>
				<div class="text-fx-grey-300 text-xs font-medium text-center">
					{addDollarSign(
						getNumBackInUnits(
							new BigNumber(tra.size).multipliedBy(tra.price).div(1e18).toString(),
							18,
							2
						)
					)}
				</div>
			</div>
		</div>
		<div class="text-fx-grey-100 text-xs font-medium flex justify-center my-auto">
			{addDollarSign(
				getNumBackInUnits(tra.price, 18, marketsConfig[tra.asset].showDecimals),
				marketsConfig[tra.asset].showDecimals
			)}
		</div>
		<div
			class="text-fx-grey-100 text-xs font-medium text-center flex items-end gap-1 justify-center whitespace-nowrap"
		>
			<div class="text-xs font-medium text-center">
				{#if tra.accruedFunding}
					{#if new BigNumber(tra.accruedFunding).gt(0)}
						<span class="text-fx-gradient-23">
							{addDollarSign(getNumBackInUnits(tra.accruedFunding, 18, 2))}
						</span>
					{:else if new BigNumber(tra.accruedFunding).lt(0)}
						<span class="text-fx-gradient-22">
							{addDollarSign(getNumBackInUnits(tra.accruedFunding, 18, 2))}
						</span>
					{:else}
						{addDollarSign(getNumBackInUnits(tra.accruedFunding, 18, 2))}
					{/if}
				{:else}
					-
				{/if}
			</div>
		</div>
		<div
			class="text-fx-grey-100 text-xs font-medium text-right flex items-end gap-1 justify-end whitespace-nowrap"
		>
			<div class="text-xs font-medium text-center">
				{#if new BigNumber(tra.pnl).gt(0)}
					<span class="text-fx-gradient-23">
						{addDollarSign(getNumBackInUnits(tra.pnl, 18, 2))}
					</span>
				{:else if new BigNumber(tra.pnl).lt(0)}
					<span class="text-fx-gradient-22">
						{addDollarSign(getNumBackInUnits(tra.pnl, 18, 2))}
					</span>
				{:else}
					{addDollarSign(getNumBackInUnits(tra.pnl, 18, 2))}
				{/if}
			</div>
			<img loading="lazy" src={'link-grey.png'} alt="etherscan" class="cursor-pointer w-5 h-5" />
		</div>
	</div>

	<!-- Mobile View -->
	<div class="sm:hidden block mb-1 p-1">
		<GradientBorder
			gradient="bg-fx-gradient-6"
			paddingClass="p-px"
			borderRadius="rounded-xl "
			outerClassList=" w-full h-full"
			innerClassList="flex justify-center items-center bg-fx-black w-full h-full"
		>
			<div class="bg-fx-gradient-8 w-full h-gull rounded-xl p-4 flex-col">
				<div class="flex justify-between mb-1">
					<div class="flex-col items-start mb-2">
						<div class="flex gap-2">
							<img
								loading="lazy"
								src={getTokenLogo(tra.marketIdentifier)}
								alt="logo"
								class="w-5 h-5 rounded-full"
							/>
							<div class="text-fx-grey-100 text-sm rounded-lg font-medium">
								{getDisplayName(tra.marketIdentifier)}
							</div>
						</div>
						<div class="flex gap-2">
							<div class="text-fx-grey-100 text-xs font-medium text-center">
								{getNumBackInUnits(tra.size, 18, 4)}
								{tra.asset}
							</div>
							<div class="text-fx-grey-300 text-xs font-medium text-center">
								(${getNumBackInUnits(
									new BigNumber(tra.size).multipliedBy(tra.price).div(1e18).abs().toString(),
									18,
									2
								)})
							</div>
						</div>
					</div>
					<div class="flex flex-col items-start justify-center">
						<GradientBorder
							gradient="bg-fx-gradient-6"
							paddingClass="p-px"
							borderRadius="rounded-lg "
							outerClassList="w-fit h-fit"
							innerClassList="/''bg-fx-black"
						>
							{#if tra.orderType === 'Liquidation'}
								<div
									class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-16 text-xs font-medium rounded-lg"
								>
									{tra.orderType}
								</div>
							{:else if new BigNumber(tra.size).gt(0)}
								<div
									class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-23 text-xs font-medium rounded-lg"
								>
									Long
								</div>
							{:else}
								<div
									class="bg-fx-gradient-8 px-4 py-1 text-fx-gradient-22 text-xs font-medium rounded-lg"
								>
									Short
								</div>
							{/if}
						</GradientBorder>
					</div>
				</div>

				<div class="grid grid-cols-3">
					<div class="flex-col">
						<div class="text-xs text-fx-grey-400">Price</div>
						<div class="text-sm text-fx-grey-200 font-medium">
							${getNumBackInUnits(tra.price, 18, marketsConfig[tra.asset]?.showDecimals)}
						</div>
					</div>
					<div class="flex-col">
						<div class="text-xs text-fx-grey-400 text-center">PnL</div>
						<div class="text-sm text-fx-grey-200 font-medium text-center">
							{#if new BigNumber(tra.pnl).gt(0)}
								<span class="text-fx-gradient-23">
									{addDollarSign(getNumBackInUnits(tra.pnl, 18, 2))}
								</span>
							{:else if new BigNumber(tra.pnl).lt(0)}
								<span class="text-fx-gradient-22">
									{addDollarSign(getNumBackInUnits(tra.pnl, 18, 2))}
								</span>
							{:else}
								{addDollarSign(getNumBackInUnits(tra.pnl, 18, 2))}
							{/if}
						</div>
					</div>
					<div class="flex-col">
						<div class="text-xs text-fx-grey-400 text-end">Accrued Funding</div>
						<div class="text-sm text-fx-grey-200 font-medium text-end">
							{#if tra.accruedFunding}
								{#if new BigNumber(tra.accruedFunding).gt(0)}
									<span class="text-fx-gradient-23">
										{addDollarSign(getNumBackInUnits(tra.accruedFunding, 18, 2))}
									</span>
								{:else if new BigNumber(tra.accruedFunding).lt(0)}
									<span class="text-fx-gradient-22">
										{addDollarSign(getNumBackInUnits(tra.accruedFunding, 18, 2))}
									</span>
								{:else}
									{addDollarSign(getNumBackInUnits(tra.accruedFunding, 18, 2))}
								{/if}
							{:else}
								-
							{/if}
						</div>
					</div>
				</div>
			</div>
		</GradientBorder>
	</div>
</a>
